import { Component, NgZone, OnInit } from '@angular/core';
import { Select } from '@ngxs/store';
import { ToastService } from '@services/common/toast.service';
import { OrganizationState } from '@state/organization.state';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { TagEntitiesDto } from 'app/@types/dto/tag-entities-dto.model';
import { TreeNode } from 'primeng/api';
import { combineLatest, Observable } from 'rxjs';
import { RowNode } from './edit-tag/edit-tag.component';

type Column = {
    field: string;
    header: string;
};
@Component({
    selector: 'app-settings-form-tag',
    templateUrl: './settings-form-tag.component.html',
    styleUrls: ['./settings-form-tag.component.scss'],
    standalone: false,
})
export class SettingsFormTagComponent implements OnInit {
    cols: Column[];
    public tags: TreeNode[] = [];
    public entities: OrganizationEntityDto[] = [];
    public expandDataTable = false;
    public treeViewData: TreeNode[] = [];
    public possibleParentTags: TagEntitiesDto[] = [];
    public possibleChildTags: TagEntitiesDto[] = [];
    public organizationId: string = '';
    public isEditMode: boolean = false;
    public selectedTag: RowNode | undefined = undefined;

    @Select(OrganizationState.getTagsOfOrganization) tags$!: Observable<TagEntitiesDto[]>;
    @Select(OrganizationState.getEntities) entities$!: Observable<OrganizationEntityDto[]>;
    @Select(OrganizationState.getOrganization) organization$!: Observable<OrganizationDto>;

    constructor(
        private toastService: ToastService,
        private ngZone: NgZone,
    ) {
        // Headers are translation keys.
        this.cols = [
            { field: 'name', header: 'settings.analytical.table.name' },
            { field: 'description', header: 'settings.analytical.table.description' },
            { field: 'entities', header: 'settings.analytical.table.linkedEntities' },
            { field: 'action', header: '' },
        ];
    }

    ngOnInit(): void {
        combineLatest([this.tags$, this.entities$, this.organization$]).subscribe(([tags, entities, organization]) => {
            if (tags && entities && organization && organization.uid) {
                this.entities = entities.filter((entity) => !entity.deleted);
                tags.sort((a, b) => a.tag.name.localeCompare(b.tag.name));
                this.tags = this.transformToTreeTableFormat(tags);
                this.possibleParentTags = tags;
                this.possibleChildTags = tags.filter((tag) => tag.childTags?.length === 0);
                this.organizationId = organization.uid;
            }
        });
    }

    transformToTreeTableFormat(tags: TagEntitiesDto[]): TreeNode[] {
        const tag = tags.sort((a, b) => a.tag.name.localeCompare(b.tag.name));

        return tag.map((tag) => ({
            data: {
                id: tag.tag.id,
                name: tag.tag.name,
                description: tag.tag.description,
                parentId: tag.tag.parentId,
                entities: tag.entityIds.map((id) => this.entities.find((entity) => entity.id === id)),
            },
            children: tag.childTags ? this.transformToTreeTableFormat(tag.childTags) : undefined,
        }));
    }

    toggleForm(rowData: RowNode | undefined = undefined) {
        this.selectedTag = rowData;
        this.isEditMode = !this.isEditMode;
    }

    onCancelEdit() {
        this.isEditMode = false;
    }

    onSaveEdit(event: { translateKey: string; status: boolean } | undefined) {
        this.isEditMode = false;
        if (event) {
            this.ngZone.run(() => {
                if (event.status) {
                    this.toastService.showSuccess({
                        details: event.translateKey,
                    });
                } else {
                    this.toastService.showError({
                        details: event.translateKey,
                    });
                }
            });
        }
    }

    trackByField(_index: number, col: Column): string {
        return col.field;
    }

    trackByEntityId(_index: number, entity: OrganizationEntityDto): string {
        return entity.id ?? '';
    }

    getColumnWidth(field: string): string {
        const widths = {
            name: '32%',
            description: '32%',
            entities: '31%',
            action: '5%',
        };
        return (widths as Record<string, string>)[field] || 'auto';
    }

    /** The entities beyond the first two, one per line (the tooltip keeps line breaks). */
    getEntityTooltipContent(entities: OrganizationEntityDto[]): string {
        return entities.map((entity) => entity.name).join('\n');
    }
}
